import { useEffect, useState } from 'react';

import { ApiClient, ApiError } from '../api/client';
import { copyText } from '../clipboard';
import type { Installer } from '../api/types';
import { formatToman, toJalaliDate, toPersianDigits } from '../format';

/* Installer accounts.
 *
 * Everything here is admin-only, on the server as well as in this UI. An
 * installer has no route to create an account, edit one, or change any password
 * including their own — that is the point of the design, not an oversight, and
 * the API returns 403 for all of it regardless of what this screen does.
 *
 * A password is shown ONCE, at the moment it is set, because the only copy after
 * that is the hash. The screen keeps it on display until dismissed rather than
 * flashing a toast: it is about to be read down a phone, and losing it means
 * resetting again.
 */

interface Props {
  api: ApiClient;
  onAuthLost: () => void;
}

export function InstallersScreen({ api, onAuthLost }: Props) {
  const [rows, setRows] = useState<Installer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const [creating, setCreating] = useState(false);
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');

  /** The one-time password, kept on screen until dismissed. */
  const [issued, setIssued] = useState<{ who: string; password: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const copyPassword = async (pw: string) => {
    setCopied(await copyText(pw));
    setTimeout(() => setCopied(false), 1800);
  };

  const guard = async (fn: () => Promise<void>) => {
    setError(null);
    try {
      await fn();
    } catch (e) {
      if (e instanceof ApiError && e.isUnauthenticated) return onAuthLost();
      setError(e instanceof Error ? e.message : 'خطای ناشناخته');
    }
  };

  const load = () => guard(async () => setRows(await api.installers()));

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = () =>
    guard(async () => {
      const created = await api.createInstaller({
        username: username.trim(),
        fullName: fullName.trim(),
        phone: phone.trim() || undefined,
      });
      setIssued({ who: created.installer.fullName, password: created.password });
      setCreating(false);
      setUsername('');
      setFullName('');
      setPhone('');
      await load();
    });

  const saveEdit = (id: number) =>
    guard(async () => {
      setBusyId(id);
      try {
        await api.updateInstaller(id, {
          fullName: editName.trim(),
          phone: editPhone.trim() || null,
        });
        setEditingId(null);
        await load();
      } finally {
        setBusyId(null);
      }
    });

  const toggleActive = (row: Installer) =>
    guard(async () => {
      setBusyId(row.id);
      try {
        await api.setInstallerActive(row.id, !row.active);
        await load();
      } finally {
        setBusyId(null);
      }
    });

  const resetPassword = (row: Installer) =>
    guard(async () => {
      setBusyId(row.id);
      try {
        const { password } = await api.setInstallerPassword(row.id);
        setIssued({ who: row.fullName, password });
        await load();
      } finally {
        setBusyId(null);
      }
    });

  const unlock = (row: Installer) =>
    guard(async () => {
      setBusyId(row.id);
      try {
        await api.unlockInstaller(row.id);
        await load();
      } finally {
        setBusyId(null);
      }
    });

  if (rows === null && !error) return <div className="card muted">در حال بارگذاری…</div>;

  return (
    <div>
      {error && <div className="alert error">{error}</div>}

      {issued && (
        <div className="card">
          <div className="muted">رمز {issued.who} — فقط همین یک بار نمایش داده می‌شود</div>
          <div className="bigcode" style={{ marginTop: 10 }}>{issued.password}</div>
          <div className="row" style={{ marginTop: 14, justifyContent: 'space-between' }}>
            <span className="muted">
              حروف مبهم (I، L، O، U) در آن نیست تا پای تلفن اشتباه شنیده نشود.
            </span>
            <div className="row">
              <button onClick={() => void copyPassword(issued.password)}>{copied ? 'کپی شد' : 'کپی'}</button>
              <button className="primary" onClick={() => setIssued(null)}>یادداشت کردم</button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="spread">
          <strong>نصاب‌ها</strong>
          <button className="primary" onClick={() => setCreating(!creating)}>
            {creating ? 'انصراف' : 'نصاب جدید'}
          </button>
        </div>

        {creating && (
          <div style={{ marginTop: 16 }}>
            <label>
              <span>نام کاربری — بعداً قابل تغییر نیست</span>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                dir="ltr"
                placeholder="hassan.m"
                autoFocus
              />
            </label>
            <label>
              <span>نام و نام خانوادگی</span>
              <input value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </label>
            <label>
              <span>شماره تماس (اختیاری)</span>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} dir="ltr" />
            </label>
            <button
              className="primary"
              disabled={username.trim().length < 3 || fullName.trim().length < 2}
              onClick={() => void create()}
            >
              ساختن حساب
            </button>
            {/* Said here rather than discovered later: the username is what every
                audit entry and settlement is filed under. */}
            <div className="muted" style={{ marginTop: 10, fontSize: 13 }}>
              رمز پس از ساختن یک‌بار نمایش داده می‌شود. نصاب نمی‌تواند خودش رمزش را عوض کند.
            </div>
          </div>
        )}
      </div>

      {rows && rows.length === 0 && <div className="card muted center">هنوز نصابی ثبت نشده است.</div>}

      {rows?.map((row) => (
        <div className="card" key={row.id}>
          <div className="spread">
            <div>
              <div style={{ fontSize: 17 }}>
                {row.fullName}{' '}
                <span className="mono muted" style={{ fontSize: 14 }}>{row.username}</span>
              </div>
              <div className="muted" style={{ fontSize: 13 }}>
                {row.phone ? toPersianDigits(row.phone) : '—'} · از {toJalaliDate(row.createdAt)}
              </div>
            </div>
            <div style={{ textAlign: 'left' }}>
              <span className={`pill ${row.active ? 'ok' : 'expired'}`}>
                {row.active ? 'فعال' : 'غیرفعال'}
              </span>
              {row.locked && <span className="pill warning" style={{ marginRight: 6 }}>قفل شده</span>}
              {/* Owed but not yet approved. Shown next to the switch because
                  turning someone off while they owe money is a decision worth
                  making with the number in view. */}
              <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                بدهی: {formatToman(row.outstanding)}
              </div>
            </div>
          </div>

          {editingId === row.id ? (
            <div style={{ marginTop: 14 }}>
              <label>
                <span>نام</span>
                <input value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus />
              </label>
              <label>
                <span>شماره تماس</span>
                <input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} dir="ltr" />
              </label>
              <div className="row">
                <button className="primary" disabled={busyId === row.id} onClick={() => void saveEdit(row.id)}>
                  ذخیره
                </button>
                <button onClick={() => setEditingId(null)}>انصراف</button>
              </div>
            </div>
          ) : (
            <div className="row" style={{ marginTop: 14 }}>
              <button
                onClick={() => {
                  setEditingId(row.id);
                  setEditName(row.fullName);
                  setEditPhone(row.phone ?? '');
                }}
              >
                ویرایش
              </button>
              <button disabled={busyId === row.id} onClick={() => void resetPassword(row)}>
                رمز جدید
              </button>
              {row.locked && (
                <button disabled={busyId === row.id} onClick={() => void unlock(row)}>
                  باز کردن قفل
                </button>
              )}
              <button
                className={row.active ? 'danger' : ''}
                disabled={busyId === row.id}
                onClick={() => void toggleActive(row)}
              >
                {row.active ? 'غیرفعال کردن' : 'فعال کردن'}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
